// src/pages/shared/ChatPage.tsx
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useRedux';
import { getConversation, sendMessage, markMessageAsRead, editMessage, deleteMessage, Conversation, Message, MessageAttachment } from '../../services/chatService';
import { uploadChatMediaFile, validateChatMediaFile } from '../../services/chatMediaService';
import MediaViewer from '../../components/common/MediaViewer';
import {
  collectConversationMedia,
  findConversationMediaIndex,
  toViewerItems,
} from '../../utils/chatMediaGallery';
import { getBaseUrlWithoutApi } from '../../config/api';
import { ArrowBackIcon, PersonIcon } from '../../components/common/Icons';
import { useUI } from '../../context/UIContext';
import { getToken as getSessionToken } from '../../utils/sessionManager';
import { io, Socket } from 'socket.io-client';
import { useTypingEmitter, TYPING_PREVIEW } from '../../hooks/useTypingEmitter';
import { getMessageReceiptState } from '../../utils/messageReceiptStatus';
import {
  canCopyMessage,
  canDeleteMessage,
  canEditMessage,
  getMessageDisplayContent,
  isMessageDeleted,
} from '../../utils/messageActions';
import UserContextPanel from '../../components/support/UserContextPanel';
import '../../styles/ChatPage.css';

const OBJECT_ID_PATTERN = /^[a-fA-F0-9]{24}$/;

type PendingMedia = {
  file: File;
  previewUrl: string;
  type: 'image' | 'video';
};

const ChatPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const { user, token: reduxToken } = useAppSelector(state => state.auth);
  const { showSnackbar } = useUI();
  
  const [messageText, setMessageText] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [attaching, setAttaching] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [showUserContext, setShowUserContext] = useState(false);
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const [actionMenu, setActionMenu] = useState<{ message: Message; x: number; y: number } | null>(null);
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [pendingMedia, setPendingMedia] = useState<PendingMedia | null>(null);
  const pendingMediaRef = useRef<PendingMedia | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const readMessagesRef = useRef<Set<string>>(new Set());
  const shouldScrollToLatestRef = useRef(true);
  const loadingOlderRef = useRef(false);
  
  // Maximum message length (matches backend)
  const MAX_MESSAGE_LENGTH = 5000;
  
  const userId = user?.id || (user as any)?._id;
  // Canonical auth source: in-memory Redux/SessionManager token (matches apiClient),
  // not localStorage — access tokens are never persisted there.
  const token = reduxToken || getSessionToken();

  const emitTypingStart = useCallback((id: string) => {
    socketRef.current?.emit('typing_start', { conversationId: id });
  }, []);

  const emitTypingStop = useCallback((id: string) => {
    socketRef.current?.emit('typing_stop', { conversationId: id });
  }, []);

  const { notifyTyping, stopTyping } = useTypingEmitter(
    conversationId,
    emitTypingStart,
    emitTypingStop
  );

  const conversationMedia = useMemo(() => collectConversationMedia(messages), [messages]);
  const viewerItems = useMemo(() => toViewerItems(conversationMedia), [conversationMedia]);

  const openMediaViewer = (messageId: string, src: string) => {
    const index = findConversationMediaIndex(conversationMedia, messageId, src);
    if (index < 0) return;
    setViewerIndex(index);
    setViewerOpen(true);
  };

  // Connect to Socket.IO and fetch messages
  useEffect(() => {
    if (!token || !userId || !conversationId) {
      setLoading(false);
      return;
    }

    const SOCKET_URL = getBaseUrlWithoutApi();
    console.log('Connecting to Socket.IO at:', SOCKET_URL);
    
    // Connect to Socket.IO
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket'],
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
      forceNew: false,
    });

    // Connection event handlers
    socketRef.current.on('connect', () => {
      console.log('✅ Socket connected:', socketRef.current?.id);
      // Join the conversation room after connection is established
      if (socketRef.current && conversationId) {
        socketRef.current.emit('joinConversation', conversationId);
        console.log(`✅ Joined room ${conversationId}`);
      }
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error.message);
      setError('Connection error. Attempting to reconnect...');
      // Don't show error to user, Socket.IO will auto-reconnect
    });

    socketRef.current.on('error', (error: any) => {
      console.error('❌ Socket error:', error);
      const errorMessage = error?.message || 'Connection error occurred';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('⚠️ Socket disconnected:', reason);
      if (reason === 'io server disconnect') {
        // Server disconnected the socket, reconnect manually
        socketRef.current?.connect();
      }
    });

    socketRef.current.on('reconnect', (attemptNumber) => {
      console.log(`✅ Socket reconnected after ${attemptNumber} attempts`);
      // Rejoin the conversation room after reconnection
      if (socketRef.current && conversationId) {
        socketRef.current.emit('joinConversation', conversationId);
      }
    });

    // Listen for new messages
    socketRef.current.on('newMessage', (message: Message) => {
      console.log('Received new message via socket:', message);
      if (
        message.conversation === conversationId &&
        message.sender !== userId &&
        OBJECT_ID_PATTERN.test(message._id)
      ) {
        socketRef.current?.emit('message_delivered', {
          conversationId,
          messageId: message._id,
        });
      }

      setMessages((prev) => {
        // Check if message already exists (by _id or by content + sender + timestamp)
        const exists = prev.some((m) => {
          if (m._id === message._id) return true;
          // Also check for optimistic messages that might have been replaced
          if (m.content === message.content && 
              m.sender === message.sender && 
              Math.abs(new Date(m.createdAt).getTime() - new Date(message.createdAt).getTime()) < 5000) {
            return true;
          }
          return false;
        });
        if (exists) {
          console.log('Message already exists, skipping');
          return prev;
        }
        console.log('Adding new message to state');
        shouldScrollToLatestRef.current = true;
        return [...prev, message];
      });
    });

    socketRef.current.on('userTyping', (data: { conversationId?: string; userId?: string }) => {
      if (data.conversationId === conversationId && data.userId && data.userId !== userId) {
        setIsOtherTyping(true);
      }
    });

    socketRef.current.on('userStoppedTyping', (data: { conversationId?: string; userId?: string }) => {
      if (data.conversationId === conversationId && data.userId && data.userId !== userId) {
        setIsOtherTyping(false);
      }
    });

    socketRef.current.on('message_status', (data: {
      messageId: string;
      conversationId: string;
      deliveredAt?: string | null;
      readAt?: string | null;
    }) => {
      if (data.conversationId !== conversationId) return;

      setMessages((prev) =>
        prev.map((message) =>
          message._id === data.messageId
            ? {
                ...message,
                deliveredAt: data.deliveredAt ?? message.deliveredAt,
                readAt: data.readAt ?? message.readAt,
              }
            : message
        )
      );
    });

    socketRef.current.on('messageEdited', (data: {
      messageId: string;
      conversationId: string;
      content: string;
      editedAt: string;
    }) => {
      if (data.conversationId !== conversationId) return;

      setMessages((prev) =>
        prev.map((message) =>
          message._id === data.messageId
            ? {
                ...message,
                content: data.content,
                editedAt: data.editedAt,
              }
            : message
        )
      );
    });

    socketRef.current.on('messageDeleted', (data: {
      messageId: string;
      conversationId: string;
      deletedAt: string;
    }) => {
      if (data.conversationId !== conversationId) return;

      setMessages((prev) =>
        prev.map((message) =>
          message._id === data.messageId
            ? {
                ...message,
                content: '',
                attachments: [],
                deletedAt: data.deletedAt,
              }
            : message
        )
      );
    });

    // Fetch existing messages with retry logic
    const fetchConversationWithRetry = async (retries = 3) => {
      try {
        const data = await getConversation(conversationId);
        if (data?.conversation && data?.messages) {
          setConversation(data.conversation);
          setMessages(data.messages.slice().reverse());
          setHasMore(data.hasMore);
          shouldScrollToLatestRef.current = true;
          setError(null);
        }
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching conversation:', err);
        const errorMessage = err?.response?.data?.error || err?.message || 'Failed to load conversation';
        
        if (retries > 0 && (err?.response?.status >= 500 || err?.code === 'ECONNREFUSED')) {
          // Retry on server errors or connection errors
          console.log(`Retrying conversation fetch... (${retries} attempts remaining)`);
          setTimeout(() => fetchConversationWithRetry(retries - 1), 1000 * (4 - retries));
        } else {
          setError(errorMessage);
          showSnackbar(errorMessage, 'error');
          setLoading(false);
        }
      }
    };

    fetchConversationWithRetry();

    return () => {
      if (socketRef.current) {
        console.log('Cleaning up socket connection');
        if (conversationId) {
          socketRef.current.emit('typing_stop', { conversationId });
          socketRef.current.emit('leaveConversation', conversationId);
        }
        socketRef.current.off('newMessage');
        socketRef.current.off('userTyping');
        socketRef.current.off('userStoppedTyping');
        socketRef.current.off('message_status');
        socketRef.current.off('messageEdited');
        socketRef.current.off('messageDeleted');
        socketRef.current.off('connect');
        socketRef.current.off('connect_error');
        socketRef.current.off('disconnect');
        socketRef.current.off('reconnect');
        if (socketRef.current.connected) {
          socketRef.current.disconnect();
        }
        socketRef.current = null;
      }
      stopTyping();
    };
  }, [conversationId, token, userId, showSnackbar, stopTyping]);

  useEffect(() => {
    if (shouldScrollToLatestRef.current && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      shouldScrollToLatestRef.current = false;
    }
  }, [messages]);

  const loadOlderMessages = useCallback(async () => {
    if (!conversationId || !hasMore || loadingOlderRef.current || messages.length === 0) {
      return;
    }

    const oldestLoaded = messages[0];
    if (!OBJECT_ID_PATTERN.test(oldestLoaded._id)) return;

    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;

    loadingOlderRef.current = true;
    setLoadingOlder(true);

    try {
      const data = await getConversation(conversationId, { before: oldestLoaded._id });
      const older = data.messages.slice().reverse();

      setMessages((prev) => {
        const existing = new Set(prev.map((m) => m._id));
        const newOlder = older.filter((m) => !existing.has(m._id));
        return [...newOlder, ...prev];
      });
      setHasMore(data.hasMore);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop += newScrollHeight - prevScrollHeight;
          }
        });
      });
    } catch (err) {
      console.error('Error loading older messages:', err);
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [conversationId, hasMore, messages]);

  const handleMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || loadingOlderRef.current || !hasMore) return;
    if (container.scrollTop <= 80) {
      loadOlderMessages();
    }
  }, [hasMore, loadOlderMessages]);

  // Mark unread messages as read when viewing the chat
  useEffect(() => {
    if (!userId || !conversationId) return;
    
    // Mark all unread messages as read when the chat page is viewed
    const markAllAsRead = async () => {
      const unreadMessages = messages.filter(
        (msg) =>
          msg.sender !== userId &&
          !msg.readBy.includes(userId) &&
          !readMessagesRef.current.has(msg._id) &&
          /^[a-fA-F0-9]{24}$/.test(msg._id)
      );

      // Mark all unread messages as read in parallel
      const markPromises = unreadMessages.map((msg) =>
        markMessageAsRead(msg._id)
          .then((updatedMsg) => {
            setMessages((prev) =>
              prev.map((m) => (m._id === updatedMsg._id ? updatedMsg : m))
            );
            readMessagesRef.current.add(msg._id);
          })
          .catch((err) => console.error('Error marking message as read:', err))
      );

      await Promise.all(markPromises);

      // Trigger a refresh of unread counts in the parent component
      // This will be handled by the DashboardLayout's interval refresh
    };

    // Only mark as read if we have messages and the page is visible
    if (messages.length > 0 && document.visibilityState === 'visible') {
      markAllAsRead();
    }
  }, [messages, userId, conversationId]);

  const MAX_BUBBLE_MEDIA_WIDTH = 240;

  const getBubbleMediaSize = (width?: number, height?: number) => {
    if (!width || !height) {
      return { width: MAX_BUBBLE_MEDIA_WIDTH, height: MAX_BUBBLE_MEDIA_WIDTH * 0.75 };
    }
    const scale = Math.min(1, MAX_BUBBLE_MEDIA_WIDTH / Math.max(width, height));
    return {
      width: Math.round(width * scale),
      height: Math.round(height * scale),
    };
  };

  const sendAttachmentMessage = async (file: File, existingTempId?: string, caption = '') => {
    if (!token || !userId || !conversationId) return;

    const tempId = existingTempId || `temp_${Date.now()}_${Math.random()}`;
    const localUri = URL.createObjectURL(file);
    const isVideo = file.type.startsWith('video/');
    const optimisticAttachment: MessageAttachment = {
      type: isVideo ? 'video' : 'image',
      url: localUri,
      thumbnailUrl: localUri,
    };

    if (!existingTempId) {
      const optimisticMsg: Message = {
        _id: tempId,
        conversation: conversationId,
        sender: userId,
        content: caption,
        attachments: [optimisticAttachment],
        readBy: [userId],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        _localStatus: 'uploading',
        _localUri: localUri,
        _failedFile: file,
      };
      setMessages((prev) => [...prev, optimisticMsg]);
      shouldScrollToLatestRef.current = true;
    } else {
      setMessages((prev) =>
        prev.map((m) =>
          m._id === tempId
            ? {
                ...m,
                _localStatus: 'uploading',
                _localUri: localUri,
                attachments: [optimisticAttachment],
                _failedFile: file,
              }
            : m
        )
      );
    }

    setAttaching(true);

    try {
      const uploaded = await uploadChatMediaFile(file);
      setMessages((prev) =>
        prev.map((m) =>
          m._id === tempId ? { ...m, _localStatus: 'sending' } : m
        )
      );

      const msg = await sendMessage(conversationId, caption, [uploaded]);
      setMessages((prev) => {
        const filtered = prev.filter((m) => m._id !== tempId);
        const exists = filtered.some((m) => m._id === msg._id);
        if (!exists) {
          return [...filtered, msg];
        }
        return filtered;
      });
      shouldScrollToLatestRef.current = true;
    } catch (err: any) {
      console.error('Error sending attachment:', err);
      setMessages((prev) =>
        prev.map((m) =>
          m._id === tempId ? { ...m, _localStatus: 'failed', _failedFile: file } : m
        )
      );
      showSnackbar(err?.message || 'Failed to send media', 'error');
    } finally {
      setAttaching(false);
      URL.revokeObjectURL(localUri);
    }
  };

  const clearPendingMedia = useCallback(() => {
    setPendingMedia((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }
      return null;
    });
  }, []);

  useEffect(() => {
    pendingMediaRef.current = pendingMedia;
  }, [pendingMedia]);

  useEffect(() => () => {
    if (pendingMediaRef.current?.previewUrl) {
      URL.revokeObjectURL(pendingMediaRef.current.previewUrl);
    }
  }, []);

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    try {
      await validateChatMediaFile(file);
    } catch (err: any) {
      showSnackbar(err?.message || 'Invalid media file', 'error');
      return;
    }

    clearPendingMedia();
    const previewUrl = URL.createObjectURL(file);
    setPendingMedia({
      file,
      previewUrl,
      type: file.type.startsWith('video/') ? 'video' : 'image',
    });
  };

  const renderAttachment = (
    attachment: MessageAttachment,
    message: Message,
    isMyMessage: boolean
  ) => {
    const displayUri = message._localUri || attachment.thumbnailUrl || attachment.url;
    const { width, height } = getBubbleMediaSize(attachment.width, attachment.height);
    const isUploading =
      message._localStatus === 'uploading' || message._localStatus === 'sending';
    const isFailed = message._localStatus === 'failed';

    const canOpenViewer = !isUploading && !isFailed && !!attachment.url;

    return (
      <div
        key={`${message._id}-${attachment.url}`}
        className="chat-message__attachment"
        style={{ width, height }}
      >
        {canOpenViewer ? (
          <button
            type="button"
            className="chat-message__attachment-button"
            onClick={() => openMediaViewer(message._id, attachment.url)}
            aria-label={
              attachment.type === 'video' ? 'Play video full screen' : 'View image full screen'
            }
          >
            <img src={displayUri} alt="" className="chat-message__attachment-media" />
            {attachment.type === 'video' && !isUploading && (
              <span className="chat-message__play-icon" aria-hidden="true">
                ▶
              </span>
            )}
          </button>
        ) : (
          <img src={displayUri} alt="" className="chat-message__attachment-media" />
        )}
        {isUploading && (
          <div className="chat-message__upload-overlay">
            <div className="spinner-small" />
          </div>
        )}
        {isFailed && isMyMessage && (
          <div className="chat-message__failed-actions">
            <button
              type="button"
              onClick={() => message._failedFile && sendAttachmentMessage(message._failedFile, message._id)}
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => setMessages((prev) => prev.filter((m) => m._id !== message._id))}
            >
              Remove
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleSendMessage = async (options?: { fromSendButton?: boolean }) => {
    // Validation
    if (!token || !userId || !conversationId) {
      showSnackbar('You must be logged in to send messages', 'error');
      return;
    }

    const trimmedContent = messageText.trim();

    if (!trimmedContent && !pendingMedia) {
      return;
    }

    if (pendingMedia) {
      if (!options?.fromSendButton) {
        return;
      }

      const file = pendingMedia.file;
      const caption = trimmedContent;
      clearPendingMedia();
      setMessageText('');
      stopTyping();
      await sendAttachmentMessage(file, undefined, caption);
      return;
    }

    // Validate message length
    if (trimmedContent.length > MAX_MESSAGE_LENGTH) {
      showSnackbar(`Message is too long (max ${MAX_MESSAGE_LENGTH} characters)`, 'error');
      return;
    }

    // Validate conversation ID format
    if (!/^[a-fA-F0-9]{24}$/.test(conversationId)) {
      showSnackbar('Invalid conversation ID', 'error');
      return;
    }

    // Basic XSS protection (client-side)
    const dangerousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
    ];
    
    for (const pattern of dangerousPatterns) {
      if (pattern.test(trimmedContent)) {
        showSnackbar('Message contains potentially unsafe content', 'error');
        return;
      }
    }

    const messageContent = trimmedContent;
    setMessageText(''); // Clear input immediately
    stopTyping();
    setSending(true);
    setError(null);

    // Create optimistic message
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    const optimisticMsg: Message = {
      _id: tempId,
      conversation: conversationId,
      sender: userId,
      content: messageContent,
      readBy: [userId],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Immediately update local state
    setMessages((prev) => [...prev, optimisticMsg]);
    shouldScrollToLatestRef.current = true;

    // Persist the message via API (this will trigger the backend to emit via Socket.IO)
    try {
      const msg = await sendMessage(conversationId, messageContent);
      console.log('Message sent successfully, received from API:', msg);
      
      // Replace optimistic message with real message
      setMessages((prev) => {
        // Remove optimistic message and add real one
        const filtered = prev.filter((m) => m._id !== tempId);
        // Check if message already exists (might have been received via socket)
        const exists = filtered.some((m) => m._id === msg._id);
        if (!exists) {
          return [...filtered, msg];
        }
        return filtered;
      });
      shouldScrollToLatestRef.current = true;
      
      setRetryCount(0);
      setSending(false);
    } catch (err: any) {
      console.error('Error sending message:', err);
      
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to send message';
      const statusCode = err?.response?.status;
      
      // Handle rate limiting
      if (statusCode === 429) {
        const retryAfter = err?.response?.data?.retryAfter || 60;
        showSnackbar(`Too many messages. Please wait ${retryAfter} seconds before trying again.`, 'error');
      } else if (statusCode === 400) {
        // Validation error
        showSnackbar(errorMessage, 'error');
      } else if (statusCode === 401 || statusCode === 403) {
        // Authentication/authorization error
        showSnackbar('You are not authorized to send messages in this conversation', 'error');
        navigate('/login');
      } else if (statusCode >= 500 || err?.code === 'ECONNREFUSED') {
        // Server error or connection error - retry
        if (retryCount < 3) {
          const newRetryCount = retryCount + 1;
          setRetryCount(newRetryCount);
          showSnackbar(`Failed to send. Retrying... (${newRetryCount}/3)`, 'warning');
          
          setTimeout(() => {
            setMessageText(messageContent);
            handleSendMessage();
          }, 1000 * newRetryCount);
          return;
        } else {
          showSnackbar('Failed to send message after multiple attempts. Please try again later.', 'error');
        }
      } else {
        showSnackbar(errorMessage, 'error');
      }
      
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m._id !== tempId));
      // Restore message text
      setMessageText(messageContent);
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (pendingMedia) {
        return;
      }
      void handleSendMessage();
    }
  };

  const closeActionMenu = () => setActionMenu(null);

  const handleCopyMessage = async (message: Message) => {
    if (!canCopyMessage(message)) return;
    try {
      await navigator.clipboard.writeText(message.content);
      showSnackbar('Message copied', 'success');
    } catch {
      showSnackbar('Could not copy message', 'error');
    }
    closeActionMenu();
  };

  const handleEditMessage = (message: Message) => {
    setEditingMessage(message);
    setEditText(message.content);
    closeActionMenu();
  };

  const handleSaveEdit = async () => {
    if (!editingMessage) return;
    const trimmed = editText.trim();
    if (!trimmed) {
      showSnackbar('Message cannot be empty', 'error');
      return;
    }

    setSavingEdit(true);
    try {
      const updated = await editMessage(editingMessage._id, trimmed);
      setMessages((prev) =>
        prev.map((message) => (message._id === updated._id ? { ...message, ...updated } : message))
      );
      setEditingMessage(null);
      setEditText('');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || err?.message || 'Could not edit message', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteMessage = async (message: Message) => {
    closeActionMenu();
    if (!window.confirm('Delete this message for everyone in the chat?')) {
      return;
    }

    try {
      const deleted = await deleteMessage(message._id);
      setMessages((prev) =>
        prev.map((item) =>
          item._id === deleted._id
            ? {
                ...item,
                content: '',
                attachments: [],
                deletedAt: deleted.deletedAt,
              }
            : item
        )
      );
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || err?.message || 'Could not delete message', 'error');
    }
  };

  const openMessageActionMenu = (event: React.MouseEvent, message: Message) => {
    if (isMessageDeleted(message) || message._localStatus || !OBJECT_ID_PATTERN.test(message._id)) {
      return;
    }

    const hasActions =
      canCopyMessage(message) || canEditMessage(message, userId) || canDeleteMessage(message, userId);
    if (!hasActions) return;

    event.preventDefault();
    setActionMenu({ message, x: event.clientX, y: event.clientY });
  };

  useEffect(() => {
    if (!actionMenu) return;

    const handleClick = () => closeActionMenu();
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [actionMenu]);

  const renderReceiptLabel = (message: Message, isMyMessage: boolean) => {
    if (!isMyMessage || !userId || isMessageDeleted(message)) return null;

    const receiptState = getMessageReceiptState(message, userId);

    if (receiptState === 'read') {
      return <span className="chat-message__receipt chat-message__receipt--read">Read</span>;
    }

    if (receiptState === 'delivered') {
      return <span className="chat-message__receipt">Delivered</span>;
    }

    if (receiptState === 'sent') {
      return <span className="chat-message__receipt">Sent</span>;
    }

    return null;
  };

  // Check if current user is a support agent
  const isSupportAgent = user?.userType === 'admin' && (user as any)?.adminRole === 'supportAgent';

  // Get other participant info
  const getOtherParticipant = () => {
    if (!conversation || !user) return null;
    
    // Find the other participant (not the current user)
    const otherParticipantId = conversation.participants.find(
      (p: string | any) => {
        const id = typeof p === 'string' ? p : (p?._id || p);
        return id !== userId;
      }
    );
    
    // For now, return a placeholder - in a real app, you'd fetch user details
    if (!otherParticipantId) return null;
    
    const id = typeof otherParticipantId === 'string' 
      ? otherParticipantId 
      : (otherParticipantId as any)?._id || otherParticipantId;
    
    // Try to get name from populated participant object
    let name = 'User';
    if (typeof otherParticipantId === 'object' && otherParticipantId !== null) {
      name = (otherParticipantId as any).name || name;
    }
    
    return {
      id,
      name,
    };
  };

  const otherParticipant = getOtherParticipant();

  if (loading) {
    return (
      <div className="chat-page">
        <div className="chat-page__loading">
          <div className="spinner"></div>
          <p>Loading conversation...</p>
        </div>
      </div>
    );
  }

  if (!token || !userId) {
    return (
      <div className="chat-page">
        <div className="chat-page__error">
          <p>You must be logged in to view this conversation.</p>
          <button onClick={() => navigate('/login')}>Go to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-page ${showUserContext && isSupportAgent ? 'chat-page--with-context' : ''}`}>
      {/* Main Chat Container */}
      <div className="chat-page__main">
        {/* Chat Header */}
        <div className="chat-page__header">
          <button className="chat-page__back" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </button>
          <div className="chat-page__header-info">
            <h2 className="chat-page__title">
              {conversation?.type === 'support' ? 'Support Chat' : 
               conversation?.type === 'appointment' ? 'Appointment Chat' : 
               'Chat'}
            </h2>
            {otherParticipant && (
              <p className={`chat-page__subtitle${isOtherTyping ? ' chat-page__subtitle--typing' : ''}`}>
                {isOtherTyping ? TYPING_PREVIEW : otherParticipant.name}
              </p>
            )}
          </div>
          {isSupportAgent && otherParticipant && (
            <button
              className="chat-page__context-toggle"
              onClick={() => setShowUserContext(!showUserContext)}
              title={showUserContext ? 'Hide user info' : 'Show user info'}
            >
              <PersonIcon size={20} />
            </button>
          )}
        </div>

      {/* Messages Area */}
      <div
        className="chat-page__messages"
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
      >
        {loadingOlder && (
          <div className="chat-page__loading-older">
            <div className="spinner-small" />
          </div>
        )}
        {messages.length === 0 ? (
          <div className="chat-page__empty">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => {
            const isMyMessage = message.sender === userId;
            const deleted = isMessageDeleted(message);
            const attachments = deleted ? [] : message.attachments || [];
            const displayContent = getMessageDisplayContent(message);
            const showText = deleted || !!displayContent;

            return (
              <div
                key={message._id}
                className={`chat-message ${isMyMessage ? 'chat-message--outgoing' : 'chat-message--incoming'}`}
              >
                <div
                  className={`chat-message__bubble${deleted ? ' chat-message__bubble--deleted' : ''}`}
                  onContextMenu={(event) => openMessageActionMenu(event, message)}
                >
                  {attachments.map((attachment) =>
                    renderAttachment(attachment, message, isMyMessage)
                  )}
                  {showText ? (
                    <p className={`chat-message__text${deleted ? ' chat-message__text--deleted' : ''}`}>
                      {displayContent}
                    </p>
                  ) : null}
                  <div className="chat-message__meta">
                    <span className="chat-message__time">
                      {new Date(message.createdAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {message.editedAt ? ' (edited)' : ''}
                    </span>
                    {renderReceiptLabel(message, isMyMessage)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error Display */}
      {error && (
        <div className="chat-page__error-banner">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Message Input */}
      <div className="chat-page__composer">
        {pendingMedia && (
          <div className="chat-page__pending-media">
            <div className="chat-page__pending-media-thumb-wrap">
              {pendingMedia.type === 'video' ? (
                <video
                  src={pendingMedia.previewUrl}
                  className="chat-page__pending-media-thumb"
                  muted
                  playsInline
                />
              ) : (
                <img
                  src={pendingMedia.previewUrl}
                  alt="Attachment preview"
                  className="chat-page__pending-media-thumb"
                />
              )}
              {pendingMedia.type === 'video' && (
                <span className="chat-page__pending-media-play" aria-hidden="true">
                  ▶
                </span>
              )}
              <button
                type="button"
                className="chat-page__pending-media-remove"
                onClick={clearPendingMedia}
                aria-label="Remove attachment"
              >
                ×
              </button>
            </div>
            <p className="chat-page__pending-media-hint">Click Send to share this attachment</p>
          </div>
        )}

        <div className="chat-page__input-area">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          className="chat-page__file-input"
          onChange={handleFileSelected}
          disabled={sending || attaching || loading}
        />
        <button
          type="button"
          className="chat-page__attach-button"
          onClick={() => fileInputRef.current?.click()}
          disabled={sending || attaching || loading}
          title="Attach photo or video"
        >
          +
        </button>
        <textarea
          className="chat-page__input"
          placeholder={pendingMedia ? 'Add a caption...' : 'Type your message...'}
          value={messageText}
          onChange={(e) => {
            const value = e.target.value;
            if (value.length <= MAX_MESSAGE_LENGTH) {
              setMessageText(value);
              notifyTyping(value);
            } else {
              showSnackbar(`Message cannot exceed ${MAX_MESSAGE_LENGTH} characters`, 'warning');
            }
          }}
          onKeyPress={handleKeyPress}
          rows={1}
          maxLength={MAX_MESSAGE_LENGTH}
          disabled={sending || loading}
        />
        <div className="chat-page__input-footer">
          {messageText.length > 0 && (
            <span className="chat-page__char-count">
              {messageText.length}/{MAX_MESSAGE_LENGTH}
            </span>
          )}
        </div>
        <button
          type="button"
          className="chat-page__send-button"
          onClick={() => {
            void handleSendMessage({ fromSendButton: true });
          }}
          disabled={(!messageText.trim() && !pendingMedia) || sending || attaching || loading}
          title={sending ? 'Sending...' : 'Send message'}
        >
          {sending ? (
            <div className="spinner-small"></div>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          )}
        </button>
        </div>
      </div>
      </div>

      {/* User Context Panel (for support agents only) */}
      {isSupportAgent && otherParticipant && showUserContext && (
        <div className="chat-page__context-panel">
          <UserContextPanel
            userId={otherParticipant.id}
            onClose={() => setShowUserContext(false)}
          />
        </div>
      )}

      {actionMenu && (
        <div
          className="chat-message__action-menu"
          style={{ top: actionMenu.y, left: actionMenu.x }}
          onClick={(event) => event.stopPropagation()}
        >
          {canCopyMessage(actionMenu.message) && (
            <button type="button" onClick={() => void handleCopyMessage(actionMenu.message)}>
              Copy
            </button>
          )}
          {canEditMessage(actionMenu.message, userId) && (
            <button type="button" onClick={() => handleEditMessage(actionMenu.message)}>
              Edit
            </button>
          )}
          {canDeleteMessage(actionMenu.message, userId) && (
            <button type="button" className="chat-message__action-menu-delete" onClick={() => void handleDeleteMessage(actionMenu.message)}>
              Delete
            </button>
          )}
        </div>
      )}

      {editingMessage && (
        <div className="chat-page__edit-modal">
          <div className="chat-page__edit-modal-card">
            <h3>Edit message</h3>
            <textarea
              value={editText}
              onChange={(event) => setEditText(event.target.value)}
              rows={4}
              disabled={savingEdit}
            />
            <div className="chat-page__edit-modal-actions">
              <button
                type="button"
                onClick={() => {
                  setEditingMessage(null);
                  setEditText('');
                }}
                disabled={savingEdit}
              >
                Cancel
              </button>
              <button type="button" onClick={() => void handleSaveEdit()} disabled={savingEdit}>
                {savingEdit ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <MediaViewer
        items={viewerItems}
        open={viewerOpen}
        index={viewerIndex}
        onClose={() => setViewerOpen(false)}
      />
    </div>
  );
};

export default ChatPage;

